'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { getListingHref, type MLSProperty } from '@/lib/listings';
import PropertyVitals from '@/components/PropertyVitals';

interface AgentListingsGridProps {
  activeListings: MLSProperty[];
  soldListings: MLSProperty[];
  mlsWithVideos?: string[];
  mlsWithMatterport?: string[];
}

function formatPrice(price: number | null): string {
  if (!price) return 'Price N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

function PropertyCard({ listing, isSold, hasVideo = false, hasMatterport = false }: { listing: MLSProperty; isSold?: boolean; hasVideo?: boolean; hasMatterport?: boolean }) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const photos = listing.photos && listing.photos.length > 0 ? listing.photos : [];
  // Only show gallery navigation for active listings, not sold
  const hasMultiplePhotos = !isSold && photos.length > 1;
  const currentPhoto = isSold ? (photos[0] || null) : (photos[currentPhotoIndex] || null);

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImageError(false);
    setCurrentPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImageError(false);
    setCurrentPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const href = getListingHref(listing);

  return (
    <div className="group relative block">
      <div className="relative aspect-square bg-[#f5f5f5] overflow-hidden">
        {/* Link covers the image area only, sits below the carousel
            buttons via z-index so prev/next clicks don't navigate. */}
        <Link
          href={href}
          aria-label={listing.address || 'View property'}
          className="absolute inset-0 z-10"
        >
          <span className="sr-only">{listing.address || 'View property'}</span>
        </Link>

        {currentPhoto && !imageError ? (
          <Image
            src={currentPhoto}
            alt={listing.address || 'Property'}
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#c0c0c0]">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
        )}

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500 pointer-events-none" />

        {hasMultiplePhotos && (
          <>
            <button
              type="button"
              onClick={handlePrevPhoto}
              className="absolute z-20 left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1a1a1a] shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
              aria-label="Previous photo"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleNextPhoto}
              className="absolute z-20 right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1a1a1a] shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
              aria-label="Next photo"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute z-20 bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              {photos.slice(0, 5).map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentPhotoIndex ? 'bg-white' : 'bg-white/50'}`}
                />
              ))}
              {photos.length > 5 && (
                <span className="text-white text-[10px] ml-1">+{photos.length - 5}</span>
              )}
            </div>
          </>
        )}

        {/* Video / Virtual Tour flags - upper right */}
        {!isSold && (hasVideo || hasMatterport) && (
          <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5">
            {hasVideo && (
              <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-navy,#002349)] text-white flex items-center gap-1.5">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Video
              </span>
            )}
            {hasMatterport && (
              <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-gold,#c19b5f)] text-white flex items-center gap-1.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Virtual Tour
              </span>
            )}
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {isSold && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-light bg-[#1a1a1a] text-white">
              Sold
            </span>
          )}
          {!isSold && listing.status?.toLowerCase().startsWith('pending') && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-amber-500 text-white">
              Pending
            </span>
          )}
          {!isSold && listing.listing_date && (() => {
            const daysDiff = Math.floor((Date.now() - new Date(listing.listing_date).getTime()) / (1000 * 60 * 60 * 24));
            return daysDiff <= 14;
          })() && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-gold,#c19b5f)] text-white">
              New Listing
            </span>
          )}
          {!isSold && listing.open_house_date && new Date(listing.open_house_date + 'T23:59:59') >= new Date() && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-navy,#002349)] text-white">
              Open House {new Date(listing.open_house_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      </div>

      <Link href={href} className="block pt-4 pb-2">
        <div className="text-xl font-light text-[#1a1a1a] dark:text-white tracking-wide mb-2">
          {isSold && listing.sold_price ? (
            <>
              {formatPrice(listing.sold_price)}
              {listing.list_price && listing.list_price !== listing.sold_price && (
                <span className="text-sm text-[#6a6a6a] line-through ml-2">
                  {formatPrice(listing.list_price)}
                </span>
              )}
            </>
          ) : (
            formatPrice(listing.list_price)
          )}
        </div>
        <p className="text-sm text-[#4a4a4a] dark:text-gray-300 font-light line-clamp-1">
          {listing.address || 'Address not available'}
        </p>
        {(listing.city || listing.state || listing.zip_code) && (
          <p className="text-sm text-[#6a6a6a] dark:text-gray-400 font-light mb-2">
            {[listing.city, listing.state].filter(Boolean).join(', ')}
            {listing.zip_code && ` ${listing.zip_code}`}
          </p>
        )}
        <PropertyVitals
          bedrooms={listing.bedrooms}
          bathrooms={listing.bathrooms}
          squareFeet={listing.square_feet}
          acres={listing.lot_size}
          className="text-sm text-[#4a4a4a] dark:text-gray-300 font-light"
        />
      </Link>
    </div>
  );
}

export default function AgentListingsGrid({ activeListings, soldListings, mlsWithVideos = [], mlsWithMatterport = [] }: AgentListingsGridProps) {
  const [activeTab, setActiveTab] = useState<'active' | 'sold'>(
    activeListings.length > 0 ? 'active' : 'sold'
  );

  const listings = activeTab === 'active' ? activeListings : soldListings;
  const hasActive = activeListings.length > 0;
  const hasSold = soldListings.length > 0;

  if (!hasActive && !hasSold) {
    return null;
  }

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-8 border-b border-gray-200 mb-10">
        {hasActive && (
          <button
            onClick={() => setActiveTab('active')}
            className={`pb-4 text-[11px] uppercase tracking-[0.2em] font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-[var(--color-gold)] border-b-2 border-[var(--color-gold)]'
                : 'text-[#6a6a6a] hover:text-[#1a1a1a]'
            }`}
          >
            Active Listings ({activeListings.length})
          </button>
        )}
        {hasSold && (
          <button
            onClick={() => setActiveTab('sold')}
            className={`pb-4 text-[11px] uppercase tracking-[0.2em] font-medium transition-colors ${
              activeTab === 'sold'
                ? 'text-[var(--color-gold)] border-b-2 border-[var(--color-gold)]'
                : 'text-[#6a6a6a] hover:text-[#1a1a1a]'
            }`}
          >
            Sold Properties ({soldListings.length})
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 md:gap-x-8 md:gap-y-12">
        {listings.map((listing) => (
          <PropertyCard
            key={listing.id}
            listing={listing}
            isSold={activeTab === 'sold'}
            hasVideo={!!listing.mls_number && mlsWithVideos.includes(listing.mls_number)}
            hasMatterport={!!listing.mls_number && mlsWithMatterport.includes(listing.mls_number) || !!listing.virtual_tour_url}
          />
        ))}
      </div>
    </div>
  );
}
